function normalizeMoney(value) {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.round(parsed * 100) / 100);
}

function normalizePeopleRanges(ranges) {
  return (Array.isArray(ranges) ? ranges : [])
    .map((range) => {
      const minPeople =
        Math.max(1, Number(range?.min_people ?? range?.minPeople ?? 1) || 1);
      const rawMax = range?.max_people ?? range?.maxPeople;
      const parsedMax =
        rawMax === "" || rawMax == null ? null : Number(rawMax);

      return {
        min_people: minPeople,
        max_people:
          Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null,
        price: normalizeMoney(range?.price ?? 0),
      };
    })
    .sort((left, right) => left.min_people - right.min_people);
}

function normalizeSelectedVehicles(selectedVehicles, locale = "en") {
  return (Array.isArray(selectedVehicles) ? selectedVehicles : [])
    .map((vehicle) => ({
      value: vehicle?.value ?? vehicle?.tuktukUUID ?? null,
      label: String(vehicle?.label ?? vehicle?.model ?? "Vehicle").trim() || "Vehicle",
      capacity: Math.max(
        0,
        Number(vehicle?.capacity ?? vehicle?.maxPeople ?? 0) || 0
      ),
      units: Math.max(
        0,
        Number(vehicle?.units ?? vehicle?.requestedUnits ?? vehicle?.internalUnits ?? 0) || 0
      ),
    }))
    .filter((vehicle) => vehicle.units > 0)
    .sort((left, right) => {
      const byCapacity = right.capacity - left.capacity;
      if (byCapacity !== 0) return byCapacity;
      return left.label.localeCompare(right.label, locale, {
        sensitivity: "base",
      });
    });
}

function resolvePriceForOccupancy(ranges, people, fallbackUnitPrice) {
  const fallback = normalizeMoney(fallbackUnitPrice);
  if (!ranges.length) return fallback;
  if (!Number.isFinite(people) || people <= 0) return ranges[0]?.price ?? fallback;

  const match = ranges.find(
    (range) =>
      people >= range.min_people &&
      (range.max_people == null || people <= range.max_people)
  );

  return match ? normalizeMoney(match.price) : fallback;
}

function allocatePackedPeopleAcrossVehicles(expanded, totalPeople) {
  let remaining = Math.max(0, Number(totalPeople) || 0);
  if (!expanded.length) return expanded;

  for (let index = 0; index < expanded.length && remaining > 0; index += 1) {
    expanded[index].assignedPeople = 1;
    remaining -= 1;
  }

  for (let index = 0; index < expanded.length && remaining > 0; index += 1) {
    const extraCapacity = Math.max(
      0,
      expanded[index].capacity - expanded[index].assignedPeople
    );
    if (extraCapacity <= 0) continue;

    const assignedExtra = Math.min(extraCapacity, remaining);
    expanded[index].assignedPeople += assignedExtra;
    remaining -= assignedExtra;
  }

  if (remaining > 0) {
    expanded[0].assignedPeople += remaining;
  }

  return expanded;
}

function allocateBalancedPeopleAcrossVehicles(expanded, totalPeople) {
  let remaining = Math.max(0, Number(totalPeople) || 0);
  if (!expanded.length) return expanded;

  while (remaining > 0) {
    let targetIndex = -1;

    for (let index = 0; index < expanded.length; index += 1) {
      const capacity = Number(expanded[index].capacity) || 0;
      const assigned = Number(expanded[index].assignedPeople) || 0;
      if (capacity > 0 && assigned >= capacity) continue;

      if (targetIndex < 0) {
        targetIndex = index;
        continue;
      }

      const targetAssigned = Number(expanded[targetIndex].assignedPeople) || 0;
      const targetCapacity = Number(expanded[targetIndex].capacity) || 0;

      if (assigned < targetAssigned) {
        targetIndex = index;
        continue;
      }

      if (assigned === targetAssigned && capacity > targetCapacity) {
        targetIndex = index;
      }
    }

    if (targetIndex < 0) {
      expanded[0].assignedPeople += remaining;
      break;
    }

    expanded[targetIndex].assignedPeople += 1;
    remaining -= 1;
  }

  return expanded;
}

function allocatePeopleAcrossVehicles(
  selectedVehicles,
  totalPeople,
  preferLowerTier = false,
  locale = "en"
) {
  const expanded = [];
  normalizeSelectedVehicles(selectedVehicles, locale).forEach((vehicle) => {
    for (let index = 0; index < vehicle.units; index += 1) {
      expanded.push({
        value: vehicle.value,
        label: vehicle.label,
        capacity: vehicle.capacity,
        assignedPeople: 0,
      });
    }
  });

  if (!expanded.length) return expanded;

  return preferLowerTier
    ? allocateBalancedPeopleAcrossVehicles(expanded, totalPeople)
    : allocatePackedPeopleAcrossVehicles(expanded, totalPeople);
}

export function calculateVehicleTierPricing({
  peopleRanges,
  selectedVehicles,
  totalPeople,
  fallbackUnitPrice = 0,
  preferLowerTier = false,
  locale = "en",
}) {
  const ranges = normalizePeopleRanges(peopleRanges);
  const fallback = normalizeMoney(fallbackUnitPrice);
  const allocations = allocatePeopleAcrossVehicles(
    selectedVehicles,
    totalPeople,
    preferLowerTier,
    locale
  );

  if (!allocations.length) {
    const unitPrice = ranges.length
      ? resolvePriceForOccupancy(
          ranges,
          Math.max(1, Number(totalPeople) || 0),
          fallback
        )
      : fallback;

    return {
      usesPeopleRanges: ranges.length > 0,
      fallbackUnitPrice: fallback,
      averageUnitPrice: unitPrice,
      vehicleCount: unitPrice > 0 ? 1 : 0,
      total: unitPrice,
      breakdown:
        unitPrice > 0
          ? [
              {
                label: "Vehicle 1",
                value: null,
                capacity: 0,
                assignedPeople: Math.max(1, Number(totalPeople) || 0),
                price: unitPrice,
              },
            ]
          : [],
    };
  }

  const breakdown = allocations.map((allocation) => ({
    ...allocation,
    price: ranges.length
      ? resolvePriceForOccupancy(ranges, allocation.assignedPeople, fallback)
      : fallback,
  }));

  const total = normalizeMoney(
    breakdown.reduce(
      (sum, allocation) => sum + normalizeMoney(allocation.price),
      0
    )
  );

  return {
    usesPeopleRanges: ranges.length > 0,
    fallbackUnitPrice: fallback,
    averageUnitPrice:
      breakdown.length > 0 ? normalizeMoney(total / breakdown.length) : 0,
    vehicleCount: breakdown.length,
    total,
    breakdown,
  };
}
